import api from './api';

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName: string;
  profilePicture?: string;
  roles: string[];
  permissions: string[]; // Fine-grained permissions array
  provider: string;
  isActive: boolean;
  emailVerified: boolean;
  mfaEnabled: boolean;
  allowedCompanyIds: string[];
  allowedJurisdictions: string[];
  lastLoginAt?: string;
  loginCount: number;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  bio?: string;
}

class AuthService {
  private readonly TOKEN_KEY = 'accessToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private readonly USER_KEY = 'user';

  /**
   * Register a new user
   */
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/register', data);
    this.setSession(response.data);
    return response.data;
  }

  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    this.setSession(response.data);
    return response.data;
  }

  /**
   * Logout - revoke refresh token and clear session
   */
  async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    if (refreshToken) {
      try {
        await api.post('/auth/logout', { refreshToken });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    this.clearSession();
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<AuthResponse>('/auth/refresh', { refreshToken });
    this.setSession(response.data);
    return response.data;
  }

  /**
   * Get current user details
   */
  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/auth/me');
    this.setUser(response.data);
    return response.data;
  }

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordData): Promise<void> {
    await api.post('/auth/change-password', data);
  }

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateProfileData): Promise<User> {
    const response = await api.patch<User>('/auth/profile', data);
    this.setUser(response.data);
    return response.data;
  }

  /**
   * Revoke all user tokens (logout from all devices)
   */
  async revokeAllTokens(): Promise<void> {
    await api.post('/auth/revoke-all-tokens');
    this.clearSession();
  }

  /**
   * Request password reset email
   */
  async forgotPassword(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email });
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await api.post('/auth/reset-password', { token, newPassword });
  }

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<void> {
    await api.get(`/auth/verify-email?token=${token}`);
  }

  /**
   * Resend verification email
   */
  async resendVerification(email: string): Promise<void> {
    await api.post('/auth/resend-verification', { email });
  }

  // Session management methods

  /**
   * Set authentication session
   */
  private setSession(authResponse: AuthResponse): void {
    localStorage.setItem(this.TOKEN_KEY, authResponse.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, authResponse.refreshToken);
    localStorage.setItem(this.USER_KEY, JSON.stringify(authResponse.user));

    // Update legacy token key for backward compatibility
    localStorage.setItem('token', authResponse.accessToken);
  }

  /**
   * Clear authentication session
   */
  clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem('token'); // Legacy key
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY) || localStorage.getItem('token');
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Get stored user
   */
  getUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Set user data
   */
  private setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    const user = this.getUser();
    return user?.roles?.includes(role) || false;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    const user = this.getUser();
    return user?.roles?.some((role) => roles.includes(role)) || false;
  }

  /**
   * Check if user has all of the specified roles
   */
  hasAllRoles(roles: string[]): boolean {
    const user = this.getUser();
    return roles.every((role) => user?.roles?.includes(role)) || false;
  }

  /**
   * Check if user can access company
   */
  canAccessCompany(companyId: string): boolean {
    const user = this.getUser();
    if (!user) return false;

    // SUPER_ADMIN and ADMIN have global access
    if (this.hasAnyRole(['SUPER_ADMIN', 'ADMIN'])) {
      return true;
    }

    // Check allowed companies
    if (user.allowedCompanyIds.length > 0) {
      return user.allowedCompanyIds.includes(companyId);
    }

    return true; // No restrictions
  }

  /**
   * Check if user can access jurisdiction
   */
  canAccessJurisdiction(jurisdiction: string): boolean {
    const user = this.getUser();
    if (!user) return false;

    // SUPER_ADMIN and ADMIN have global access
    if (this.hasAnyRole(['SUPER_ADMIN', 'ADMIN'])) {
      return true;
    }

    // Check allowed jurisdictions
    if (user.allowedJurisdictions.length > 0) {
      return user.allowedJurisdictions.includes(jurisdiction);
    }

    return true; // No restrictions
  }
}

export const authService = new AuthService();
export default authService;
